/**
 * HENNE Premium Animations
 * Apple-style scroll animations with Intersection Observer
 * Optimized for Shopify Horizon Theme
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
    staggerDelay: 100
  };

  // Animation Classes
  const FADE_IN_CLASS = 'henne-fade-in';
  const STAGGER_CLASS = 'henne-stagger-item';
  const VISIBLE_CLASS = 'is-visible';

  /**
   * Initialize scroll animations
   */
  function initScrollAnimations() {
    // Check for Intersection Observer support
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all elements immediately
      document.querySelectorAll(`.${FADE_IN_CLASS}, .${STAGGER_CLASS}`).forEach(el => {
        el.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    // Create observer for fade-in elements
    const fadeObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(VISIBLE_CLASS);
          fadeObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: config.threshold,
      rootMargin: config.rootMargin
    });

    // Create observer for stagger containers
    const staggerObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const items = entry.target.querySelectorAll(`.${STAGGER_CLASS}`);
          items.forEach((item, index) => {
            setTimeout(() => {
              item.classList.add(VISIBLE_CLASS);
            }, index * config.staggerDelay);
          });
          staggerObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: config.rootMargin
    });

    // Observe fade-in elements
    document.querySelectorAll(`.${FADE_IN_CLASS}`).forEach(el => {
      fadeObserver.observe(el);
    });

    // Observe stagger containers
    document.querySelectorAll('.henne-stagger-container').forEach(container => {
      staggerObserver.observe(container);
    });

    // Also observe individual stagger items if not in a container
    document.querySelectorAll(`.${STAGGER_CLASS}`).forEach(el => {
      if (!el.closest('.henne-stagger-container')) {
        fadeObserver.observe(el);
      }
    });
  }

  /**
   * Smooth scroll for anchor links
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;
        
        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  /**
   * Parallax effect for hero sections
   */
  function initParallax() {
    const parallaxElements = document.querySelectorAll('.henne-parallax');
    if (parallaxElements.length === 0) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          
          parallaxElements.forEach(el => {
            const speed = parseFloat(el.dataset.speed) || 0.3;
            const rect = el.getBoundingClientRect();
            
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              const yPos = -(scrolled * speed);
              el.style.transform = `translate3d(0, ${yPos}px, 0)`;
            }
          });

          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * Counter animation for numbers
   */
  function initCounterAnimation() {
    const counters = document.querySelectorAll('.henne-counter');
    if (counters.length === 0) return;

    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          const target = parseInt(counter.dataset.target) || 0;
          const duration = parseInt(counter.dataset.duration) || 2000;
          const start = 0;
          const startTime = performance.now();

          function updateCounter(currentTime) {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function (ease-out)
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(start + (target - start) * easeProgress);
            
            counter.textContent = current.toLocaleString('de-DE');

            if (progress < 1) {
              requestAnimationFrame(updateCounter);
            } else {
              counter.textContent = target.toLocaleString('de-DE');
            }
          }

          requestAnimationFrame(updateCounter);
          counterObserver.unobserve(counter);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(counter => counterObserver.observe(counter));
  }

  /**
   * Initialize all animations
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initScrollAnimations();
        initSmoothScroll();
        initParallax();
        initCounterAnimation();
      });
    } else {
      initScrollAnimations();
      initSmoothScroll();
      initParallax();
      initCounterAnimation();
    }
  }

  // Check for reduced motion preference
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    // Skip animations for users who prefer reduced motion
    document.querySelectorAll(`.${FADE_IN_CLASS}, .${STAGGER_CLASS}`).forEach(el => {
      el.classList.add(VISIBLE_CLASS);
    });
  } else {
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
  });

})();
